// src/modulos/Parametros/CheckStatusPage.js
import React, { useState, useMemo, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Constantes
const STATUS_COLLECTION = 'parameters/data/situacao-cheque';
const ITEMS_PER_PAGE = 10;

const CheckStatusPage = () => {
    const can = usePagePermissions('situacao_do_cheque');

    // Estados
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);

    // Hook de Dados
    const {
        data: checkStatuses,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(STATUS_COLLECTION, 'name');

    // Filtro e Paginação
    const filteredStatuses = useMemo(() => {
        if (!checkStatuses) return [];
        if (!searchTerm) return checkStatuses;

        const lowerCaseSearch = searchTerm.toLowerCase();
        return checkStatuses.filter(item =>
            item.name?.toLowerCase().includes(lowerCaseSearch)
        );
    }, [checkStatuses, searchTerm]);

    const totalFilteredItems = filteredStatuses.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    const currentStatuses = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredStatuses.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredStatuses, currentPage]);

    // Reset do Formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;
        setDeleteLoading(true);

        try {
            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess(`Situação "${name}" deletada com sucesso!`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                // Ajustar página após exclusão
                const remainingItems = filteredStatuses.length - 1;
                if (
                    currentPage > 1 &&
                    remainingItems <= (currentPage - 1) * ITEMS_PER_PAGE
                ) {
                    setCurrentPage((prev) => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError(`Erro ao deletar: ${result.error}`);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar situação. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, deleteDocument, filteredStatuses.length, currentPage]);

    // Submissão do Formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        if (!nameInput.trim()) {
            setFormError("O nome da Situação é obrigatório.");
            return;
        }

        // Verificar duplicidade
        const existingStatus = checkStatuses?.find(
            status => status.name?.toLowerCase() === nameInput.trim().toLowerCase() &&
                status.id !== editingItem?.id
        );

        if (existingStatus) {
            setFormError("Já existe uma situação com este nome.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(`Situação ${editingItem ? 'editada' : 'adicionada'} com sucesso!`);
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, editingItem, checkStatuses, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Situação'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar'}
            >
                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Situação do Cheque <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        placeholder="Ex: Compensado, Devolvido, A Vencer"
                        required
                        className={styles.input}
                        disabled={formLoading}
                        autoFocus
                    />
                    <small className={styles.fieldHint}>
                        Nome da situação que será exibida nos registros
                    </small>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a situação "${deleteConfirm.name}"?\n\nEsta ação não poderá ser desfeita e pode afetar a integridade dos dados.`}
                isLoading={deleteLoading}
                danger={true}
            />

            {/* Page Header - FIXED: Changed can.create to can.create (already correct) */}
            <PageHeader
                title="Situação do Cheque"
                actionText="Adicionar Situação"
                onAction={handleNewClick}
                disabled={formLoading}
                showAction={can.create}
            />

            {/* Search Bar */}
            <div className={styles.searchBar}>
                <SearchBar
                    placeholder="Buscar por nome da situação..."
                    onSearch={handleSearch}
                    disabled={listLoading || formLoading}
                />
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar lista: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={2} rows={ITEMS_PER_PAGE} />
                    ) : currentStatuses.length === 0 ? (
                        <EmptyState
                            icon={searchTerm ? '🔍' : '📋'}
                            title={
                                searchTerm
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma situação cadastrada'
                            }
                            message={
                                searchTerm
                                    ? `Não encontramos resultados para "${searchTerm}"`
                                    : 'Clique em "+ Adicionar Situação" para começar'
                            }
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Situação do Cheque</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {currentStatuses.map((item) => (
                                            <tr key={item.id} className={styles.tableRow}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                    {item.updatedAt && (
                                                        <small className={styles.rowMetadata}>
                                                            Atualizado: {new Date(item.updatedAt).toLocaleDateString('pt-BR')}
                                                        </small>
                                                    )}
                                                </td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="situações"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default CheckStatusPage;